import { usePageParams } from 'hooks/use-page-params';
import { useInfiniteQuery } from 'react-query';
import http from '../config/http-service';
import type { MasterProductFamilyListResponse } from './types';
import { masterProductFamiliesEndpoint } from './use-get-master-product-families-api';

const PAGE_SIZE = 50;

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);
const num = (v: unknown): number | undefined => {
  const n = Number(v);
  return typeof v === 'string' && v !== '' && !Number.isNaN(n) ? n : undefined;
};

// Infinite-scroll variant of the families list (the Prices family pane):
// universalName + catalogCategoryId still come from the URL like the paged
// hook, but paging is internal — each fetched page appends to the list.
export const useInfiniteMasterProductFamiliesApi = () => {
  const { getPageParams } = usePageParams();
  const params = getPageParams();

  const universalName = str(params.universalName);
  const catalogCategoryId = num(params.catalogCategoryId);

  return useInfiniteQuery(
    [masterProductFamiliesEndpoint, 'infinite', universalName, catalogCategoryId],
    async ({ pageParam = 1 }): Promise<MasterProductFamilyListResponse> => {
      const { data } = await http.get(masterProductFamiliesEndpoint, {
        params: {
          page: pageParam,
          limit: PAGE_SIZE,
          universalName,
          // BE expands this to self + descendant categories, so an L1/L2
          // grouping id matches every family underneath it.
          catalogCategoryId,
        },
      });
      return data;
    },
    {
      getNextPageParam: (lastPage) => (lastPage.hasNext ? lastPage.page + 1 : undefined),
      keepPreviousData: true,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
    }
  );
};
